<template>
  <PageContent>
    <div class="w-full xl:w-2/3">
      <h1 class="text-xl md:text-2xl">
        {{ $t('direct_order.title') }}
      </h1>

      <p class="mt-3 text-base md:mt-0 md:text-lg">
        {{ $t('direct_order.instructions') }}
      </p>

      <form
        class="mt-3 flex flex-col gap-x-6 gap-y-3 md:mt-6 md:flex-row"
        @submit.prevent="submit"
      >
        <div class="flex w-full grow flex-row items-center md:w-auto">
          <TextInput
            v-model="orderNumber"
            input-id="orderNumber"
            class="w-full text-base leading-6 tracking-widest"
            label="10041608"
            :aria-label="$t('direct_order.order_number_label')"
            :has-errors="
              Boolean(submittedOrderNumber && (!validOrderNumber || error))
            "
            required
          />
        </div>
        <Button
          class="w-full px-20 py-3 text-base leading-6 shadow-button md:w-1/3"
          type="primary"
          native-type="submit"
          ignore-capitalisation
          no-padding
          :disabled="!orderNumber.trim()"
          @click="submit"
        >
          {{ $t('direct_order.search_button') }}
        </Button>
      </form>

      <p
        v-if="error.length"
        class="mt-3 inline-flex items-center gap-1 text-base text-red md:mt-6"
      >
        <IconXCircle class="size-4" />{{ error }}
      </p>
    </div>

    <Skeleton
      v-if="fetching"
      type="custom"
      class="ml-4 mt-5 size-96 md:mt-10"
    />

    <div v-else-if="product && productVariants.length" class="mt-5 md:mt-10">
      <div class="flex flex-1 flex-col items-start md:flex-row">
        <div class="w-full md:w-1/2 xl:w-2/3">
          <!-- TODO: use TransitionComponent -->
          <Transition
            enter-from-class="translate-y-8 opacity-0"
            enter-to-class="translate-0 opacity-100"
            enter-active-class="transform transition duration-500 ease-linear"
            appear
          >
            <div>
              <ProductImageGallery
                :images="product.images"
                :product-name="productName"
                :product-order-number="orderNumber"
                :product-name-rows="productNameRows"
                :product="product"
              />
            </div>
          </Transition>
        </div>

        <div
          class="sticky right-0 top-0 w-full md:top-16 md:w-1/2 lg:top-32 xl:w-1/3"
        >
          <div class="mx-auto">
            <Transition
              enter-from-class="translate-x-4 opacity-0"
              enter-to-class="translate-0 opacity-100"
              enter-active-class="transform transition duration-300 ease-linear"
              appear
            >
              <div class="w-full bg-white px-16 py-8">
                <div v-if="product.isSoldOut" class="left-0 top-0">
                  <ProductBadge
                    class="ml-0"
                    :badge-label="
                      $t(
                        `badge_labels.${getBadgeLabel({
                          isSoldOut: product.isSoldOut,
                        })}`,
                      )
                    "
                  />
                </div>

                <div class="max-w-xs">
                  <NuxtPicture
                    v-if="brandLogo"
                    width="300"
                    provider="static"
                    :modifiers="{ trim: 1 }"
                    height="100"
                    :src="brandLogo"
                  />
                  <h1 class="font-semibold leading-tight">
                    <span class="text-xl">{{ productName }}</span>
                  </h1>
                  <div
                    class="text-base text-[#6B6B6B]"
                    data-test-id="product-order-number"
                  >
                    {{ $t('pdp.main_order_number') }}
                    <span data-test-id="product-order-number">{{
                      submittedOrderNumber
                    }}</span>
                  </div>
                </div>

                <ProductPrice
                  v-if="price"
                  class="mb-1 mt-8"
                  :price="price"
                  :applied-reductions="price?.appliedReductions"
                  show-tax-info
                  :show-price-reduction-badge="hasSpecial"
                  type="whisper"
                  is-pdp
                />

                <div class="space-y-6">
                  <ProductDetailGroup :headline="{ tag: 'div', size: 'sm' }">
                    <ProductColorPicker
                      :selected-color-name="selectedColorName"
                      :items="getSiblingsColors(product) as ProductSibling[]"
                    >
                      <template #item="{ item }">
                        <ColorCircle
                          class="cursor-pointer"
                          :has-active-dot="item.id === product.id"
                          :color="item.colors[0] as ProductColor"
                          @click="handleColorChange(item.id)"
                        />
                      </template>
                    </ProductColorPicker>
                  </ProductDetailGroup>

                  <ProductDetailGroup>
                    <ProductSizePicker
                      display-size-note
                      :product="product"
                      :variants="productVariants"
                      @select-size="handleSelectedSize"
                    />
                  </ProductDetailGroup>

                  <div>
                    <div class="flex items-center space-x-2">
                      <Button
                        data-test-id="add-item-to-basket-button"
                        no-padding
                        type="primary"
                        class="grow py-2"
                        :class="{ 'cursor-not-allowed': product.isSoldOut }"
                        :disabled="!canPurchase"
                        :title="!canPurchase ? $t('badge_labels.sold_out') : ''"
                        ignore-capitalisation
                        @click="addItemToBasket"
                      >
                        {{ ctaLabel }}
                      </Button>
                    </div>

                    <div
                      v-if="deliveryEstimationDate"
                      class="mt-2.5 inline-flex items-center gap-x-2 text-base"
                    >
                      <IconCircleGreen
                        class="size-4"
                        :class="{
                          'text-green': !isDelayedDelivery,
                          'text-yellow': isDelayedDelivery,
                        }"
                      />
                      <p>
                        {{ deliveryEstimationDate }}
                      </p>
                    </div>
                  </div>

                  <ul class="my-4 columns-2">
                    <li v-for="(benefit, index) in buyingBenefits" :key="index">
                      <Icon icon="ui/checkmark" size="3" class="mr-2" />
                      <span class="align-middle text-sm">
                        {{ $t(benefit) }}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </div>
  </PageContent>
</template>

<script setup lang="ts">
import type {
  Product,
  Variant,
  FieldSet,
  ProductSibling,
  ProductColor,
} from '@scayle/storefront-nuxt'
import {
  getPrice,
  getFirstAttributeValue,
  getAttributeValue,
  isInStock,
} from '@scayle/storefront-nuxt'
import {
  getSiblingOrderNoWithMatchingPromotionKey,
  isCupSizeVariant,
  getCupSizeMatchingArticlePlacement,
} from '~/utils/product'
import useBackInStock from '~/composables/useBackInStock'
import { NuxtPicture } from '#components'
import { Action } from '#layers/base/plugins/06.toast'

const DELAYED_DELIVERY_CUTOFF_IN_DAYS = 21

const orderNumber = ref('')
const submittedOrderNumber = ref<string>('')
const flatSubmittedOrderNumber = computed(() =>
  submittedOrderNumber.value.replace(/-/g, ''),
)
const error = ref('')

useRouteCache((helper) => {
  helper.setMaxAge(3600).setCacheable()
})

const { $alert } = useNuxtApp()
const currentShop = useCurrentShop()
const i18n = useI18n()
const config = useRuntimeConfig()
const route = useRoute()
const { trackAddToBasket } = useTrackingEvents()
const { openAddToBasketFlyout, showBackInStock } = useUiState()
useBackInStock()

const metaTags = metaTagGenerator({
  canonical: sanitizeCanonical(`${config.public.baseUrl}${route.path}`),
})
const metaLinks = useStaticMetaLinks('service/direct-order')

useHead({
  ...metaTags,
  link: [...(metaTags.link || []), ...metaLinks.value],
})

const product = ref<Product>()
const activeVariant = ref<Variant>()
const pricePromotionKey = ref<string>(DEFAULT_PRICE_PROMOTION_KEY)
const initialPricePromotionKey = ref<string>(DEFAULT_PRICE_PROMOTION_KEY)
const productNameRef = ref<HTMLElement | null>(null)

const shopConfiguration = useSmartShopConfiguration()
const basket = useAgsBasket(pricePromotionKey.value)
const [{ fetching }, searchProductsByOrderNumber] = await Promise.all([
  useSearchResults(),
  useSearchProductsByOrderNumber(),
])

const promotionKeyVariants = useVariant({
  params: () => ({
    ids: product.value?.variants?.map((variant: any) => variant.id) || [],
    pricePromotionKey: pricePromotionKey.value,
    include: {
      attributes: 'all',
    },
  }),
  options: { immediate: false, watch: [] },
})

// Fetch data on navigation
await fetchOnNavigation(async () => {
  await Promise.all([shopConfiguration.fetch(), basket.fetch()])
})

const productVariants = computed(() => promotionKeyVariants.data.value || [])

const price = computed(() =>
  activeVariant.value
    ? getPrice(activeVariant.value)
    : getLowestPrice(productVariants.value),
)

const cleanOrderNumber = computed(() => {
  const prefixes = ['AKN', 'AK', 'A']
  let matchedPrefix = ''

  for (const prefix of prefixes) {
    if (orderNumber.value.startsWith(prefix)) {
      matchedPrefix = prefix
      break
    }
  }

  if (matchedPrefix) {
    const restOfString = orderNumber.value.slice(matchedPrefix.length)
    const cleanedRestOfString = restOfString.replace(/\D/g, '')

    return matchedPrefix + cleanedRestOfString
  } else {
    return orderNumber.value.replace(/\D/g, '')
  }
})

const validOrderNumber = computed(
  () =>
    cleanOrderNumber.value.length >= 5 && cleanOrderNumber.value.length <= 9,
)

const applyMatchedPromotionKey = async (
  articlePlacements: FieldSet | undefined,
  orderNumberKey: string,
): Promise<boolean> => {
  const matchedPlacement = articlePlacements
    ?.find((placement) =>
      placement?.[0]?.value?.toString().includes(`${orderNumberKey}|`),
    )?.[0]
    ?.value?.toString()
    ?.split('|')

  if (!matchedPlacement || matchedPlacement.length < 2) {
    return false
  }

  const [rawOrderNumber, promotionKey] = matchedPlacement

  if (!rawOrderNumber || !promotionKey) {
    return false
  }

  submittedOrderNumber.value = rawOrderNumber
  initialPricePromotionKey.value = promotionKey
  pricePromotionKey.value = promotionKey
  basket.setPricePromotionKey(promotionKey)

  await promotionKeyVariants.refresh()
  return true
}

const submit = async () => {
  error.value = ''
  product.value = undefined
  activeVariant.value = undefined
  const orderNo = cleanOrderNumber.value
  submittedOrderNumber.value = orderNo

  if (validOrderNumber.value) {
    const { product: productByOrderNumber, orderNumber } =
      (await searchProductsByOrderNumber.fetch({
        term: flatSubmittedOrderNumber.value,
      })) || {}

    if (productByOrderNumber && orderNumber) {
      product.value = productByOrderNumber

      const articlePlacements =
        product.value?.advancedAttributes?.articlePlacement?.values[0]?.fieldSet

      const matchedOrdernumber = articlePlacements?.find((articlePlacement) =>
        articlePlacement[0]?.value?.toString().includes(`${orderNumber}|`),
      )

      const matchingCupSizeVariant = getMatchingCupSizeVariant(
        productByOrderNumber,
        orderNumber,
      )

      if (matchedOrdernumber) {
        const success = await applyMatchedPromotionKey(
          articlePlacements,
          flatSubmittedOrderNumber.value,
        )
        if (success) {
          return
        }
      } else if (matchingCupSizeVariant) {
        submittedOrderNumber.value =
          getProductOrderNumberFromVariant(
            matchingCupSizeVariant,
          )?.toString() || orderNumber

        const success = await applyMatchedPromotionKey(
          articlePlacements,
          flatSubmittedOrderNumber.value,
        )
        if (success) {
          return
        }
      } else {
        submittedOrderNumber.value =
          getFormattedOrderNumber(productByOrderNumber) || orderNumber

        const success = await applyMatchedPromotionKey(
          articlePlacements,
          flatSubmittedOrderNumber.value,
        )
        if (success) {
          return
        }

        if (!submittedOrderNumber.value) {
          error.value = i18n.t('direct_order.error.not_found')
        }
      }
    } else {
      error.value = i18n.t('direct_order.error.invalid')
    }
  }
}

const deliveryEstimationInDays = computed(() =>
  getDeliveryEstimationInDays(shopConfiguration.data.value),
)

const deliveryEstimationDate = computed(() =>
  getDeliveryEstimationDate(
    activeVariant.value,
    deliveryEstimationInDays.value,
  ),
)

// Mirror PdpDelivery: the deliverability circle is green on time, yellow when
// delivery exceeds the cutoff. The icon fills with currentColor, so without a
// text-* class it would inherit the default (black) body color (ECOM-1452).
const isDelayedDelivery = computed(() => {
  const daysTilDelivery = getDeliveryDaysEstimation(
    activeVariant.value,
    deliveryEstimationInDays.value,
  )
  return daysTilDelivery && daysTilDelivery > DELAYED_DELIVERY_CUTOFF_IN_DAYS
})

const openBackInStockModal = () => {
  showBackInStock.value = true
}

const productName = computed(
  () =>
    getFirstAttributeValue(product.value?.attributes, 'name')?.label ||
    i18n.t('wishlist.product'),
)

const productNameRows = computed(() => {
  return productNameRef.value?.getClientRects()?.length ?? 1
})

const brandLogo = computed(() =>
  getAttributeValue(product.value?.attributes, 'brandLogo'),
)

const hasSpecial = computed(() =>
  Boolean(price.value?.appliedReductions.length),
)

const selectedColorName = computed(
  () => getFirstAttributeValue(product.value?.attributes, 'color')?.label,
)

const handleColorChange = async (id: string | number) => {
  const { data, refresh: fetchProduct } = await useProduct(
    {
      params: {
        id: Number(id),
        with: PRODUCT_DETAILS_WITH,
      },
      options: { immediate: false },
    },
    `product-${id}`,
  )

  activeVariant.value = undefined

  await fetchProduct()

  if (data.value) {
    product.value = data.value

    // Attempt to match the selected color's order number and promotion key
    const { matchingOrderNumber, matchingPromotionKey } =
      getSiblingOrderNoWithMatchingPromotionKey(
        product.value,
        Number(id),
        initialPricePromotionKey.value,
      )

    if (matchingOrderNumber && matchingPromotionKey) {
      // Update order number if a match is found
      submittedOrderNumber.value = matchingOrderNumber.toString()

      await fetchProduct()
      activeVariant.value = undefined

      if (data.value) {
        product.value = data.value
        pricePromotionKey.value = matchingPromotionKey
        await promotionKeyVariants.refresh()
        return
      }
      error.value = i18n.t('direct_order.error.not_found')
    } else {
      // Fallback to using the default order number without a matching promotion key
      const newOrderNumber = getProductOrderNumber(data.value)

      if (newOrderNumber) {
        submittedOrderNumber.value = newOrderNumber.toString()
      }

      await promotionKeyVariants.refresh()
      return
    }
  } else {
    error.value = i18n.t('direct_order.error.invalid')
  }
}

const handleSelectedSize = (variant: Variant) => {
  activeVariant.value = variant

  // Get all order numbers associated with this variant
  const orderNumberListArray = getProductOrderNumberList(variant)
  const isCupSize = isCupSizeVariant(variant)

  // Proceed only if we're dealing with a cup size
  if (orderNumberListArray.length > 0 && isCupSize) {
    // Find the correct order number based on the selected variant
    const newOrderNumber = getProductOrderNumberFromVariant(variant)

    if (newOrderNumber) {
      const articlePlacements =
        product.value?.advancedAttributes?.articlePlacement?.values[0]?.fieldSet

      const matchedPlacement = getCupSizeMatchingArticlePlacement(
        newOrderNumber,
        articlePlacements,
        pricePromotionKey.value,
        DEFAULT_PRICE_PROMOTION_KEY,
      )

      if (matchedPlacement) {
        submittedOrderNumber.value = matchedPlacement
      }
    } else {
      submittedOrderNumber.value = newOrderNumber ?? ''
    }
  }
}

const canPurchase = computed(
  () =>
    !!product.value &&
    !!productVariants.value &&
    (!product.value.isSoldOut ||
      productVariants.value.every((item) => item.stock.isSellableWithoutStock)),
)

const ctaLabel = computed(() => {
  if (activeVariant.value && !isInStock(activeVariant.value)) {
    return i18n.t('back_in_stock_subscription_form.cta_button_label')
  }

  if (!canPurchase.value) {
    return i18n.t('badge_labels.sold_out')
  }

  if (
    activeVariant.value &&
    basket.contains({ variantId: activeVariant.value.id }, basket.items.value)
  ) {
    return i18n.t('pdp.already_in_cart_label')
  }

  return i18n.t('pdp.add_label')
})

const addItemToBasket = async () => {
  if (
    activeVariant.value === undefined ||
    product.value?.variants?.every((v: any) => v.id !== activeVariant.value?.id)
  ) {
    // TODO: translation key does not fit  maybe; no_item_selected
    $alert.show(
      i18n.t('basket.notification.add_basket'),
      Action.route,
      undefined,
    )
    return
  }

  if (!isInStock(activeVariant.value)) {
    openBackInStockModal()
    return
  }

  await basket.addItem({
    variantId: activeVariant.value.id,
    quantity: 1,
    customData: {
      ordernumber: submittedOrderNumber.value,
      deliveryEstimationDate: getDeliveryEstimation(
        activeVariant.value,
        deliveryEstimationInDays.value,
      ),
    },
  })

  openAddToBasketFlyout(activeVariant.value.id)

  await basket.fetch()

  if (product.value) {
    trackAddToBasket({
      product: product.value,
      variant: activeVariant.value,
      type: 'direct_order',
    })
  }
}

const buyingBenefits = [
  'pdp.purchase_on_account',
  [10013, 10014].includes(currentShop.value.shopId) ? null : 'pdp.free_return',
  'pdp.free_consultation',
  'pdp.fast_shipping',
].filter((v) => v) as string[]

defineOptions({ name: 'DirectOrder' })
definePageMeta({ pageType: 'direct_order' })
</script>
